import React, { useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
// MODIFIED: real auth state instead of the hardcoded isAuthenticated/username placeholders
import { useAuth } from './AuthProvider';
// ADDED: drives the dark mode toggle that sits next to the username
import { useTheme } from './ThemeProvider';
// ADDED: friend request notifications
import NotificationBell from './NotificationBell';

export default function NavBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme(); // ADDED
    // ADDED: the links collapse behind a hamburger on phones
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // ADDED: links that need a session are hidden while signed out
    const links = [
        { label: 'Home', path: '/' },
        { label: 'Models', path: '/models', requiresAuth: true },
        { label: 'Collections', path: '/collections', requiresAuth: true },
        { label: 'Multipacks', path: '/multipacks', requiresAuth: true }, // ADDED
        { label: 'Friends', path: '/friends', requiresAuth: true }, // ADDED
    ].filter((link) => !link.requiresAuth || isAuthenticated);

    const handleNavigation = (route) => {
        setIsMenuOpen(false); // always close the mobile menu after navigating
        navigate(route);
    };

    // ADDED: Logout used to just navigate to /login without ending the session
    const handleAuthButton = () => {
        setIsMenuOpen(false);
        if (isAuthenticated) {
            logout();
            navigate('/');
        } else {
            navigate('/login');
        }
    };

    const isActive = (path) => location.pathname.toLowerCase() === path;

    // ADDED: the sun/moon switch. Rendered twice - once in the desktop bar and once in
    // the mobile dropdown - so it is reachable at every width.
    const themeToggle = (
        <button
            type="button"
            onClick={toggleTheme}
            role="switch"
            aria-checked={isDark}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="p-2 rounded-lg cv-body hover:text-brand-flame hover:bg-brand-flame/10 transition duration-300"
        >
            {isDark ? (
                // Sun: shown while dark, because tapping it returns you to light
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                </svg>
            )}
        </button>
    );

    return (
        // MODIFIED: the bar now uses the theme surface instead of a hardcoded white,
        // and carries the speed stripe along its bottom edge.
        <header className="sticky top-0 z-50 backdrop-blur"
                style={{ backgroundColor: 'var(--surface-raised)', borderBottom: '1px solid var(--border-subtle)' }}>
            {/* MODIFIED: "relative" so the links can be centred on the bar itself */}
            <div className="relative py-3 px-4 sm:px-8 flex justify-between items-center gap-3">

                {/* Wordmark: "SPEED" in the heading colour, "VAULT" in the brand red */}
                <button onClick={() => handleNavigation("/")}
                    className="font-display font-bold text-xl sm:text-2xl tracking-tight whitespace-nowrap">
                    <span className="cv-strong">SPEED</span><span className="cv-accent">VAULT</span>{/* MODIFIED: renamed from CLOUD VAULT */}
                </button>

                {/* MODIFIED: the links sat wherever justify-between left room, which pushed
                    them left of centre because the account controls on the right are wider
                    than the wordmark. Pinning them to the middle of the bar centres them
                    against the bar itself rather than against whatever flanks them.
                    Only from md up, where the inline list is shown at all. */}
                <ul className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
                    {links.map((link) => (
                        <li key={link.path}>
                            <button
                                onClick={() => handleNavigation(link.path)}
                                id={link.label}
                                className={`font-display text-sm font-semibold uppercase tracking-wider px-3 py-2 rounded-lg transition duration-300 ${
                                    isActive(link.path)
                                        ? 'text-brand-flame bg-brand-flame/10'
                                        : 'cv-body hover:text-brand-flame'
                                }`}>
                                {link.label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-1 sm:gap-2">
                    {/* MODIFIED: "Hi, {name}" is now a button that opens the account page */}
                    {isAuthenticated && user &&
                        <button onClick={() => handleNavigation('/account')}
                            title="Account settings"
                            className="hidden lg:flex items-center gap-2 px-2 py-1.5 rounded-lg cv-body hover:text-brand-flame hover:bg-brand-flame/10 transition duration-300">
                            <span className="h-7 w-7 rounded-full bg-brand-flame text-white flex items-center justify-center font-display text-xs font-bold shrink-0">
                                {user.name?.charAt(0).toUpperCase()}
                            </span>
                            <span className="text-sm whitespace-nowrap">Hi, {user.name}</span>
                        </button>
                    }

                    {/* ADDED: friend requests waiting for you. Signed-in only, and shown at
                        every width so it is not hidden behind the phone menu. */}
                    {isAuthenticated && user && <NotificationBell onNavigate={handleNavigation} />}

                    {/* ADDED: the dark mode toggle, sitting right beside the username */}
                    {themeToggle}

                    <button onClick={handleAuthButton}
                        className="cv-btn cv-btn-primary text-sm px-3 sm:px-4 py-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />
                        </svg>
                        <span className="hidden sm:inline">{isAuthenticated ? 'Logout' : 'Login'}</span>
                    </button>

                    {/* Hamburger, phones and small tablets only */}
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={isMenuOpen}
                        className="md:hidden p-1 cv-body">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-7 h-7">
                            {isMenuOpen
                                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />}
                        </svg>
                    </button>
                </div>
            </div>

            {/* Dropdown panel for the mobile menu */}
            {isMenuOpen && (
                <ul className="md:hidden px-4 pb-3 animate-fade-up" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    {/* MODIFIED: on phones the greeting is the way into the account page */}
                    {isAuthenticated && user && (
                        <li>
                            <button onClick={() => handleNavigation('/account')}
                                className="w-full flex items-center gap-3 py-3 text-left"
                                style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                <span className="h-9 w-9 rounded-full bg-brand-flame text-white flex items-center justify-center font-display text-sm font-bold shrink-0">
                                    {user.name?.charAt(0).toUpperCase()}
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm cv-strong truncate">Hi, {user.name}</span>
                                    <span className="block text-xs cv-muted">Account settings</span>
                                </span>
                            </button>
                        </li>
                    )}
                    {links.map((link) => (
                        <li key={link.path}>
                            <button onClick={() => handleNavigation(link.path)}
                                className={`w-full text-left font-display text-sm font-semibold uppercase tracking-wider py-3 ${isActive(link.path) ? 'text-brand-flame' : 'cv-body'}`}
                                style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                {link.label}
                            </button>
                        </li>
                    ))}
                    {/* The toggle again, spelled out with a label in the mobile menu */}
                    <li className="flex items-center justify-between pt-3">
                        <span className="text-sm cv-body">{isDark ? 'Dark mode' : 'Light mode'}</span>
                        {themeToggle}
                    </li>
                </ul>
            )}

            {/* ADDED: the three-tone speed stripe that runs under the whole bar */}
            <div className="cv-stripe" />
        </header>
    );
}
